import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { Order } from '@hero/schema';
import { LOGISTI_APP } from '@hero/apps';
import { cn } from '@hero/web-lib';
import { useGovernmentWaybillPdf } from '@hero/web-hooks';
import { AppLogo } from '../../../app/AppLogo';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/Tooltip';

export interface GovernmentWaybillRefProps {
  order: Order;
  /** Overrides the default click behaviour (open the PDF). */
  onClick?: () => void;
  className?: string;
}

/**
 * The Bayan trip id for an order, badged with the Logisti mark.
 *
 * ## Why it reads the order document and never fetches
 *
 * This renders in the orders table, one per row. A status read per row would
 * turn scrolling a page of orders into dozens of requests for a number that is
 * already denormalised onto `order.waybillBayan`. So the trip id, the archived
 * PDF url, and the decision to render at all all come off the order the caller
 * already holds.
 *
 * ## Why the mark alone, with the number in the tooltip
 *
 * `order.ref.main` is what the two parties quote to each other; the Bayan trip
 * id is what a TGA inspector at a checkpoint asks the driver for. Two numbers
 * side by side read as one ref repeated, and in a table a second line of text
 * costs height on every row including the majority with no waybill at all. So
 * the mark carries the only thing that matters at a glance — this load has a
 * government document — and the number arrives on hover, which is when someone
 * is actually being asked for it.
 */
export const GovernmentWaybillRef: React.FC<GovernmentWaybillRefProps> = ({
  order,
  onClick,
  className,
}) => {
  const { t } = useTranslation(['m-apps']);
  // No `pdfUrl` override: the archived url is already on the order, and asking
  // for a status per row is what this component exists to avoid.
  const pdf = useGovernmentWaybillPdf(order, {
    onError: () => toast.error(t('m-apps:toast.error.download_failed')),
  });

  // `waybillIds` is Bayan's per-waybill number; `tripId` is the trip an
  // inspector looks up. Prefer the trip, fall back to the waybill so a
  // document issued before Hero recorded trips still shows something.
  const reference = order.waybillBayan?.tripId ?? order.waybillBayan?.waybillIds?.[0];
  if (!reference) return null;

  const handleClick = (event: React.MouseEvent) => {
    // Every surface this lands on is itself clickable — a table row that opens
    // the drawer, a drawer header. Without this the government PDF and the row
    // action both fire.
    event.stopPropagation();
    if (onClick) {
      onClick();
      return;
    }
    pdf.open();
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        {/* Radix opens a tooltip on focus, not only on hover. This button is the
            first tabbable element in the order drawer, so the drawer's own
            open-focus would pop the tooltip over the header before the user
            touches anything. Suppressing the focus handler (composeEventHandlers
            skips Radix's once ours prevents default) leaves the button keyboard
            reachable and Enter still opens the PDF; the label it would have
            announced is already on aria-label. */}
        <TooltipTrigger asChild onFocus={event => event.preventDefault()}>
          <button
            type="button"
            onClick={handleClick}
            disabled={pdf.isPending}
            aria-label={t('m-apps:waybill.ref.aria', { reference })}
            className={cn(
              'inline-flex shrink-0 items-center rounded opacity-80 transition-opacity',
              'hover:opacity-100 disabled:cursor-wait disabled:opacity-50',
              className,
            )}>
            <AppLogo
              name={t('m-apps:logisti.name')}
              logo={LOGISTI_APP.logo}
              compact
              className="size-4 shrink-0"
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {t('m-apps:waybill.ref.tooltip', { reference })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
